/**
 * <AdminViewingBanner> — warn-tinted read-only banner for admin deal observation.
 *
 * `admin-deal-oversight` Phase 4 Session 4.5 (2026-06-03).
 *
 * Displayed above the tab body when an admin is viewing a foreign deal in
 * read-only mode via `<AdminReadOnlyBuilder>`. Uses the warn-tinted triplet
 * (`bg-warn-dim border border-warn/15 text-warn`) to visually distinguish the
 * admin observation surface from the share-recipient view (accent-tinted).
 *
 * Intentionally has NO Fork CTA — admins observe deals but do not fork them
 * (ADR 0004 — admin observes a foreign deal; no Ask-AI path).
 *
 * Carries the single "← Back to deals" affordance for ALL admin views (Builder
 * read-only + public-deal read-only), so the admin always has a way back to
 * `/admin?tab=deals` from wherever a deal opened.
 */
import clsx from 'clsx';
import { Link } from '@tanstack/react-router';

export interface AdminViewingBannerProps {
  dealTitle: string;
  ownerName: string;
  className?: string;
  /** Render the "← Back to deals" link (default true). */
  showBackToDeals?: boolean;
}

export function AdminViewingBanner({
  dealTitle,
  ownerName,
  className,
  showBackToDeals = true,
}: AdminViewingBannerProps) {
  return (
    <div
      data-testid="admin-viewing-banner"
      className={clsx(
        'flex items-center gap-2.5 px-3 py-2 rounded-[2px] text-xs',
        'bg-warn-dim border border-warn/15 text-warn',
        className
      )}
    >
      <span className="uppercase tracking-[0.5px] font-bold font-mono shrink-0">
        Admin viewing — read only
      </span>
      <span className="text-text-muted">·</span>
      <span className="text-text-bright font-mono truncate">{dealTitle}</span>
      <span className="text-text-muted shrink-0">·</span>
      <span className="text-text-muted font-mono shrink-0">by {ownerName}</span>
      {showBackToDeals && (
        <Link
          to="/admin"
          search={{ tab: 'deals' as const }}
          data-testid="admin-banner-back-to-deals"
          className="ml-auto shrink-0 font-mono font-bold text-accent hover:underline focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent rounded-[2px]"
        >
          ← Back to deals
        </Link>
      )}
    </div>
  );
}
